/**
 * GlobalPwaInstallPrompt — site-wide install-to-home-screen nudge.
 *
 * Shows on EVERY visit for any non-installed user, across:
 *   - Chrome/Edge/Android (uses native beforeinstallprompt event)
 *   - iOS Safari (shows manual "Share → Add to Home Screen" helper)
 *
 * Reminder cadence (per user directive — nudge, don't nag):
 *   - Shown on first visit
 *   - Re-shown every 3 days if the user tapped "Later"
 *   - Hidden forever if the user taps "Don't show again", installs, or
 *     is already running in standalone mode
 *
 * State keys in localStorage:
 *   pwa_install_state  = 'dismissed' | 'installed' | 'later'
 *   pwa_install_next   = ISO timestamp for next nudge
 */
import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";

const STATE_KEY = "pwa_install_state";
const NEXT_KEY = "pwa_install_next";
const REMIND_DAYS = 3;

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iosUA = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const ipadOS13 = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iosUA || ipadOS13;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function shouldShowNow() {
  const state = localStorage.getItem(STATE_KEY);
  if (state === "dismissed" || state === "installed") return false;
  const next = localStorage.getItem(NEXT_KEY);
  if (!next) return true;
  return new Date(next).getTime() <= Date.now();
}

function scheduleNextReminder() {
  const d = new Date();
  d.setDate(d.getDate() + REMIND_DAYS);
  localStorage.setItem(NEXT_KEY, d.toISOString());
  localStorage.setItem(STATE_KEY, "later");
}

export default function GlobalPwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const ios = isIos();

  useEffect(() => {
    if (isStandalone()) {
      localStorage.setItem(STATE_KEY, "installed");
      return;
    }
    if (!shouldShowNow()) return;

    // iOS path: no beforeinstallprompt — show after brief delay
    if (ios) {
      const t = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(t);
    }

    // Android/Chromium path
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(STATE_KEY, "installed");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // Fallback: if beforeinstallprompt hasn't fired in 3s, show manual hint
    const fallbackTimer = setTimeout(() => setVisible(true), 3000);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(fallbackTimer);
    };
  }, [ios]);

  if (!visible) return null;

  const later = () => {
    scheduleNextReminder();
    setVisible(false);
    setShowIosHelp(false);
  };

  const dismissForever = () => {
    localStorage.setItem(STATE_KEY, "dismissed");
    localStorage.removeItem(NEXT_KEY);
    setVisible(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (ios) {
      setShowIosHelp(true);
      return;
    }
    if (!deferred) {
      // Chromium hasn't offered native prompt yet — fall back to manual hint
      setShowIosHelp(true);
      return;
    }
    try {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") {
        localStorage.setItem(STATE_KEY, "installed");
        setVisible(false);
      } else {
        scheduleNextReminder();
        setVisible(false);
      }
    } catch {
      scheduleNextReminder();
      setVisible(false);
    }
  };

  return (
    <>
      <div
        data-testid="global-pwa-install-banner"
        className="fixed bottom-4 left-1/2 z-[9999] w-[94%] max-w-md -translate-x-1/2 rounded-2xl bg-slate-900 text-white shadow-2xl px-4 py-3 flex items-center gap-3"
      >
        <Download size={20} className="flex-shrink-0 text-emerald-400" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">Add Liantiao to your home screen</div>
          <div className="text-[11px] text-slate-300 mt-0.5 leading-snug">
            Faster sign-in, full-screen experience, works offline.
          </div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-accept"
          className="rounded-lg bg-emerald-500 hover:bg-emerald-400 active:scale-[.97] transition px-3 py-1.5 text-xs font-semibold text-white"
        >
          Install
        </button>
        <button
          onClick={later}
          aria-label="Remind me later"
          data-testid="pwa-install-later"
          className="text-slate-300 hover:text-white p-1"
          title="Remind me later"
        >
          <X size={18} />
        </button>
      </div>

      {showIosHelp && (
        <div
          data-testid="pwa-install-ios-help"
          onClick={() => setShowIosHelp(false)}
          className="fixed inset-0 z-[10000] bg-black/60 flex items-end sm:items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white text-slate-900 p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-semibold">Install on this device</h3>
              <button
                onClick={() => setShowIosHelp(false)}
                aria-label="Close"
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-xs font-bold">1</span>
                <span>
                  Tap the <strong>Share</strong> button <Share size={14} className="inline-block mx-1 align-[-2px]" />
                  at the bottom of your browser
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-xs font-bold">2</span>
                <span>
                  Scroll and tap <strong>Add to Home Screen</strong> <Plus size={14} className="inline-block mx-1 align-[-2px]" />
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-xs font-bold">3</span>
                <span>Tap <strong>Add</strong> in the top-right corner. Done.</span>
              </li>
            </ol>
            <div className="mt-5 flex flex-col gap-2">
              <button
                onClick={() => setShowIosHelp(false)}
                className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 active:scale-[.99] transition px-4 py-2.5 text-sm font-semibold text-white"
                data-testid="pwa-install-ios-ok"
              >
                Got it
              </button>
              <button
                onClick={dismissForever}
                className="w-full text-[12px] text-slate-500 hover:text-slate-800"
                data-testid="pwa-install-dismiss-forever"
              >
                Don&apos;t show this again
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
